import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const primaryCategories = categories.slice(0, 7);
  const secondaryCategories = categories.slice(7);

  return (
    <footer className="bg-foreground-900 text-background-100 relative overflow-hidden">
      {/* Decorative world map outline */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
        <div className="w-full h-full flex items-center justify-center text-[20rem] text-background-50 font-light select-none">
          &#x25C9;
        </div>
      </div>

      <div className="relative w-full px-4 md:px-8 py-14 md:py-18">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Left - Brand + Newsletter */}
          <div className="lg:w-2/5">
            <Link to="/" className="inline-block mb-6">
              <span className="font-heading text-2xl font-bold text-background-50">
                GTBuy<span className="text-primary-400">Sheet</span>
              </span>
            </Link>
            <p className="text-background-300 text-sm leading-relaxed mb-6 max-w-sm">
              Your gateway to the ultimate spreadsheet shopping experience. Browse verified deals across 14 categories, updated daily.
            </p>
            {/* Newsletter */}
            <form
              data-readdy-form
              className="flex gap-2 max-w-sm"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
                if (honeypot) return;

                const email = (form.querySelector('[name="email"]') as HTMLInputElement)?.value;
                if (!email) return;

                try {
                  const formData = new URLSearchParams();
                  formData.append('email', email);
                  formData.append('source', 'gtbuy-footer');

                  await fetch('https://readdy.ai/api/form/d92hg2nefssur58kr07g', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: formData.toString(),
                  });

                  const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
                  if (btn) {
                    btn.innerHTML = '<i class="ri-check-line w-4 h-4 flex items-center justify-center"></i>';
                    btn.disabled = true;
                  }
                } catch {
                  // silently fail
                }
              }}
            >
              <input
                type="email"
                name="email"
                placeholder="Your email for weekly deals"
                required
                className="flex-1 px-4 py-2.5 rounded-full text-sm bg-foreground-800 border border-foreground-700 text-background-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <div className="honeypot-guard">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              </div>
              <button
                type="submit"
                className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center"></i>
              </button>
            </form>
            <p className="mt-5 text-background-200 text-xl md:text-2xl font-heading italic">
              Fresh spreadsheet drops, <br />straight to your inbox.
            </p>
          </div>

          {/* Right - Link Columns */}
          <div className="lg:w-3/5 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-background-50 font-semibold text-sm mb-4">Categories A-G</h4>
              <ul className="space-y-2.5">
                {primaryCategories.map((cat) => (
                  <li key={cat.name}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-background-300 hover:text-primary-400 text-sm transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-background-50 font-semibold text-sm mb-4">Categories H-Z</h4>
              <ul className="space-y-2.5">
                {secondaryCategories.map((cat) => (
                  <li key={cat.name}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-background-300 hover:text-primary-400 text-sm transition-colors whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-background-50 font-semibold text-sm mb-4">Resources</h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/guide/gtbuy-sheet-ultimate-guide-2026" className="text-background-300 hover:text-primary-400 text-sm transition-colors whitespace-nowrap">
                    Ultimate Guide 2026
                  </Link>
                </li>
                <li>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-background-300 hover:text-primary-400 text-sm transition-colors whitespace-nowrap">
                    Main Spreadsheet
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-background-50 font-semibold text-sm mb-4">Quick Links</h4>
              <ul className="space-y-2.5">
                <li>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-background-300 hover:text-primary-400 text-sm transition-colors whitespace-nowrap">
                    GTBuy Sheet
                  </a>
                </li>
                <li>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-background-300 hover:text-primary-400 text-sm transition-colors whitespace-nowrap">
                    Buy It Now
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-14 pt-8 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-foreground-500 text-xs">
            &copy; {new Date().getFullYear()} GTBuy Sheet. All rights reserved.
          </p>
          <div className="flex items-center gap-3">
            <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg border border-foreground-700 text-foreground-400 hover:text-primary-400 hover:border-primary-400 transition-colors cursor-pointer">
              <i className="ri-twitter-x-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg border border-foreground-700 text-foreground-400 hover:text-primary-400 hover:border-primary-400 transition-colors cursor-pointer">
              <i className="ri-instagram-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg border border-foreground-700 text-foreground-400 hover:text-primary-400 hover:border-primary-400 transition-colors cursor-pointer">
              <i className="ri-discord-line w-4 h-4 flex items-center justify-center"></i>
            </a>
            <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg border border-foreground-700 text-foreground-400 hover:text-primary-400 hover:border-primary-400 transition-colors cursor-pointer">
              <i className="ri-pinterest-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
          <a href="#" className="text-foreground-500 hover:text-primary-400 text-xs transition-colors">
            Privacy Policy
          </a>
        </div>
      </div>
    </footer>
  );
}